import {
  EXPORT_CHART_OPTIONS,
  EXPORT_FORMAT_OPTIONS,
  type AnalyticsExportPopulationScope,
  type AnalyticsExportReportMetadata,
  type AnalyticsExportThroughputLineMetadata,
  type AnalyticsExportWorkerResult,
  type ExportChartAvailability,
  type ExportChartId,
  type ExportFormat,
} from "@/features/deployment-dashboard/analytics/export/types";
import { getThroughputLineDisplayName } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-trend-chart.utils";

export const ANALYTICS_EXPORT_REPORT_TITLE = "Analytics Report";

export const EXPORT_MIME_TYPES: Record<ExportFormat, string> = {
  pdf: "application/pdf",
  png: "application/zip",
  csv: "text/csv;charset=utf-8",
};

export const PNG_REPORT_LAYOUT = {
  width: 1600,
  outerHorizontalPadding: 64,
  outerTopPadding: 56,
  outerBottomPadding: 64,
  headerChartGap: 48,
  chartSectionGap: 40,
  panelPadding: 32,
  maximumChartImageHeight: 720,
  unavailablePanelHeight: 320,
} as const;

export interface ExportScale {
  width: number;
  height: number;
}

export interface AnalyticsExportMetadataInput {
  dateRangeLabel: string;
  areaLabel: string;
  exportedAtLabel: string;
  filterStartIso: string;
  filterEndIso: string;
  exportedAtIso: string;
  throughputLines?: readonly AnalyticsExportThroughputLineMetadata[];
}

export function isExportFormat(value: string): value is ExportFormat {
  return EXPORT_FORMAT_OPTIONS.some((option) => option.value === value);
}

export function isExportChartId(value: string): value is ExportChartId {
  return EXPORT_CHART_OPTIONS.some((option) => option.value === value);
}

export function normalizeSelectedCharts(selectedCharts: readonly string[]): ExportChartId[] {
  const selectedChartIds = new Set(selectedCharts.filter(isExportChartId));
  return EXPORT_CHART_OPTIONS.map((option) => option.value).filter((id) =>
    selectedChartIds.has(id)
  );
}

export function getExportChartTitle(chartId: ExportChartId): string {
  return EXPORT_CHART_OPTIONS.find((option) => option.value === chartId)?.label ?? chartId;
}

export function getUnavailableChartMessage(
  availability: ExportChartAvailability
): string | undefined {
  switch (availability) {
    case "ready":
      return undefined;
    case "loading":
      return "Chart was loading when export started.";
    case "error":
      return "Chart was unavailable because its current view was in an error state.";
    case "empty":
      return "No data was rendered for this chart.";
    case "not-configured":
      return "No throughput lines were configured for the selected area.";
    case "not-selected":
      return "No throughput line was selected.";
    case "not-rendered":
      return "No rendered chart image was available when export started.";
  }
}

export function toSafeFilenameTimestamp(exportedAtIso: string): string {
  return exportedAtIso.replace(/\.\d{3}Z$/, "Z").replaceAll(":", "-");
}

export function getExportFileExtension(format: ExportFormat): "pdf" | "zip" | "csv" {
  switch (format) {
    case "pdf":
      return "pdf";
    case "png":
      return "zip";
    case "csv":
      return "csv";
  }
}

export function getAnalyticsExportFilename(format: ExportFormat, exportedAtIso: string): string {
  return `analytics-export-${toSafeFilenameTimestamp(exportedAtIso)}.${getExportFileExtension(format)}`;
}

export function scaleToFit(
  sourceWidth: number,
  sourceHeight: number,
  maximumWidth: number,
  maximumHeight: number
): ExportScale {
  if (sourceWidth <= 0 || sourceHeight <= 0 || maximumWidth <= 0 || maximumHeight <= 0) {
    return { width: 0, height: 0 };
  }

  const scale = Math.min(maximumWidth / sourceWidth, maximumHeight / sourceHeight);
  return { width: sourceWidth * scale, height: sourceHeight * scale };
}

export function createAnalyticsExportMetadata(
  input: AnalyticsExportMetadataInput
): AnalyticsExportReportMetadata {
  return {
    dateRangeLabel: input.dateRangeLabel,
    areaLabel: input.areaLabel,
    exportedAtLabel: input.exportedAtLabel,
    filterStartIso: input.filterStartIso,
    filterEndIso: input.filterEndIso,
    exportedAtIso: input.exportedAtIso,
    ...(input.throughputLines == null
      ? {}
      : { throughputLines: input.throughputLines.map((line) => ({ ...line })) }),
  };
}

/**
 * Display names for each throughput line, qualified with the owning area name
 * when the lines span more than one area (e.g. an "All Areas" export).
 */
export function formatThroughputLineNames(
  lines: readonly AnalyticsExportThroughputLineMetadata[]
): string[] {
  const hasMultipleAreas = new Set(lines.map((line) => line.areaId)).size > 1;
  return lines.map((line) => {
    const displayName = getThroughputLineDisplayName(line.name);
    return hasMultipleAreas ? `${displayName} (${line.areaName})` : displayName;
  });
}

/** Human-readable summary of the selected throughput lines, truncated for long selections. */
export function summarizeThroughputLineNames(
  lines: readonly AnalyticsExportThroughputLineMetadata[],
  maxNames = 3
): string {
  if (lines.length === 0) return "None selected";
  const names = formatThroughputLineNames(lines);
  if (names.length <= maxNames) return names.join(", ");
  return `${names.slice(0, maxNames).join(", ")}, and ${names.length - maxNames} more`;
}

/** True when a Population Trend export excludes at least one requested area. */
export function isPartialPopulationScope(scope: AnalyticsExportPopulationScope): boolean {
  return scope.unconfiguredAreas.length > 0;
}

/**
 * One-line note for CSV rows identifying a partial Population Trend export,
 * or `undefined` when every requested area was configured and contributed.
 */
export function formatPopulationScopeNote(
  scope: AnalyticsExportPopulationScope
): string | undefined {
  if (!isPartialPopulationScope(scope)) return undefined;
  const excludedNames = scope.unconfiguredAreas.map((area) => area.area_name).join(", ");
  const excludedCount = scope.unconfiguredAreas.length;
  return `Showing Total Population for ${scope.configuredAreaIds.length} of ${scope.requestedAreaIds.length} areas. ${excludedCount} ${excludedCount === 1 ? "area is" : "areas are"} not configured (${excludedNames}).`;
}

/**
 * PDF/PNG header lines identifying a partial Population Trend export, or
 * `undefined` when every requested area was configured and contributed. The
 * on-screen chart warning is React text outside the captured ECharts image,
 * so PDF/PNG must render this explicitly rather than relying on the image.
 */
export function getPopulationScopeSummaryLines(
  scope: AnalyticsExportPopulationScope
): string[] | undefined {
  if (!isPartialPopulationScope(scope)) return undefined;
  const excludedNames = scope.unconfiguredAreas.map((area) => area.area_name).join(", ");
  return [
    `Population scope: ${scope.configuredAreaIds.length} of ${scope.requestedAreaIds.length} requested areas`,
    `Excluded: ${excludedNames}`,
  ];
}

export function isAnalyticsExportWorkerSuccess(
  value: unknown
): value is Extract<AnalyticsExportWorkerResult, { type: "success" }> {
  return (
    isRecord(value) &&
    value.type === "success" &&
    typeof Blob !== "undefined" &&
    value.file instanceof Blob
  );
}

export function isAnalyticsExportWorkerFailure(
  value: unknown
): value is Extract<AnalyticsExportWorkerResult, { type: "failure" }> {
  return isRecord(value) && value.type === "failure" && typeof value.message === "string";
}

export function isAnalyticsExportWorkerResult(
  value: unknown
): value is AnalyticsExportWorkerResult {
  return isAnalyticsExportWorkerSuccess(value) || isAnalyticsExportWorkerFailure(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
